import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../common/LoadingSpinner'
import axios from 'axios'
import { toast } from 'react-toastify'
import { format, formatDistanceToNow } from 'date-fns'

const GymDetailsModal = ({ isOpen, onClose, gymId }) => {
  const { user, hasPermission } = useAuth()
  const [gym, setGym] = useState(null)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('details')
  const [editingNotes, setEditingNotes] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [selectedStage, setSelectedStage] = useState(null)
  const [preRekiData, setPreRekiData] = useState(null)
  const [postRekiData, setPostRekiData] = useState(null)
  const [rekiLoading, setRekiLoading] = useState(false)
  const [viewingFile, setViewingFile] = useState(null)
  const [viewingFileLoading, setViewingFileLoading] = useState(false)

  const isBDExecutive = user?.role?.level === 4
  const isManager = user?.role?.level <= 3
  const isCEO = user?.role?.level === 1

  useEffect(() => {
    if (isOpen && gymId) {
      fetchGymDetails()
    }
  }, [isOpen, gymId])

  useEffect(() => {
    if (gym && gym.stage) {
      setSelectedStage(gym.stage);
    }
  }, [gym]);

  // Fetch Pre-Reki and Post-Reki data when those tabs are selected
  useEffect(() => {
    if (isOpen && gymId && (activeTab === 'preReki' || activeTab === 'postReki')) {
      fetchRekiData()
    }
  }, [isOpen, gymId, activeTab])

  const fetchRekiData = async () => {
    try {
      setRekiLoading(true)
      const response = await axios.get(`/api/reki/gym/${gymId}/pre_reki`)
      if (response.data.checklist) {
        setPreRekiData(response.data.checklist)
      }
    } catch (error) {
      console.log('No pre-reki data found:', error.response?.data?.message || error.message)
    }

    try {
      const response = await axios.get(`/api/reki/gym/${gymId}/post_reki`)
      if (response.data.checklist) {
        setPostRekiData(response.data.checklist)
      }
    } catch (error) {
      console.log('No post-reki data found:', error.response?.data?.message || error.message)
    } finally {
      setRekiLoading(false)
    }
  }

  // Reset Post-Reki to sync from Pre-Reki
  const resetPostReki = async () => {
    if (!confirm('This will delete the current Post-Reki checklist and recreate it from Pre-Reki items. Continue?')) {
      return
    }
    try {
      setRekiLoading(true)
      await axios.post(`/api/reki/reset-post-reki/${gymId}`)
      toast.success('Post-Reki reset successfully')
      // Refetch to get the new checklist
      const response = await axios.get(`/api/reki/gym/${gymId}/post_reki`)
      if (response.data.checklist) {
        setPostRekiData(response.data.checklist)
      } else {
        setPostRekiData(null)
      }
    } catch (error) {
      console.error('Error resetting Post-Reki:', error)
      toast.error(error.response?.data?.error || 'Failed to reset Post-Reki')
    } finally {
      setRekiLoading(false)
    }
  }

  const fetchGymDetails = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`/api/gyms/${gymId}`)
      setGym(response.data.gym)
    } catch (error) {
      console.error('Error fetching gym details:', error)
      toast.error('Failed to load gym details')
      onClose()
    } finally {
      setLoading(false)
    }
  }

  const handleNoteSave = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note')
      return
    }

    try {
      setSavingNote(true)
      const response = await axios.put(`/api/gyms/${gymId}`, { 
        internalNotes: gym.internalNotes ? `${gym.internalNotes}\n\n---\n${format(new Date(), 'MMM dd, yyyy HH:mm')}\n${newNote}` : newNote
      })
      
      setGym(prev => ({ ...prev, internalNotes: response.data.gym.internalNotes }))
      setNewNote('')
      setEditingNotes(false)
      toast.success('Note added successfully')
    } catch (error) {
      console.error('Error saving note:', error)
      toast.error('Failed to save note')
    } finally {
      setSavingNote(false)
    }
  }

  // Get the URL for a media item
  // Uses media.url if available, otherwise constructs path from uploadFolder and filename
  // Also fixes legacy URLs that are missing the subfolder (e.g., /uploads/stage-proof-xxx.pdf -> /uploads/stage-proofs/stage-proof-xxx.pdf)
  const getMediaUrl = (media, uploadFolder = 'stage-proofs') => {
    if (media.url) {
      // Fix legacy URLs that are missing the stage-proofs subfolder
      // Pattern: /uploads/stage-proof-xxx should be /uploads/stage-proofs/stage-proof-xxx
      if (media.url.startsWith('/uploads/stage-proof-') && !media.url.includes('/stage-proofs/')) {
        return media.url.replace('/uploads/stage-proof-', '/uploads/stage-proofs/stage-proof-');
      }
      return media.url;
    }
    return `/uploads/${uploadFolder}/${media.filename}`;
  };

  // Download file function
  // uploadFolder: 'stage-proofs' or 'reki' (only used if media.url is not set)
  const downloadFile = async (media, uploadFolder = 'stage-proofs') => {
    try {
      const fileUrl = getMediaUrl(media, uploadFolder);
      const response = await axios.get(fileUrl, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', media.originalName || media.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const canDeleteMedia = (media) => {
    if (!user || !media) return false
    if (user.role?.level <= 3) return true
    if (user.role?.level === 5 && media.uploadedById === user.id) return true
    return false
  }

  const deleteMedia = async (media) => {
    if (!canDeleteMedia(media)) {
      toast.error('You do not have permission to delete this file')
      return
    }
    const label = media.originalName || media.filename || 'this file'
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return
    try {
      await axios.delete(`/api/media/${media.id}`)
      setGym(prev => prev ? { ...prev, media: (prev.media || []).filter(m => m.id !== media.id) } : prev)
      toast.success('File deleted')
    } catch (error) {
      console.error('Delete error:', error)
      toast.error(error.response?.data?.error || 'Failed to delete file')
    }
  }

  // View file function - fetches with auth and opens in viewer
  // uploadFolder: 'stage-proofs' or 'reki' (only used if media.url is not set)
  const viewFile = async (media, uploadFolder = 'stage-proofs') => {
    const fileUrl = getMediaUrl(media, uploadFolder);
    const name = media.originalName || media.filename || '';
    const ext = name.split('.').pop()?.toLowerCase() || '';

    const isVideo = media.mimetype?.startsWith('video/')
      || media.type === 'VIDEO'
      || ['mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v', '3gp', 'ogv'].includes(ext);
    const isImage = media.mimetype?.startsWith('image/')
      || media.type === 'IMAGE'
      || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
    const isPdf = media.mimetype === 'application/pdf' || ext === 'pdf';

    // Videos: serve straight from the static URL. Fetching ~50MB as a blob just to
    // re-attach it to <video src> hangs the UI and wastes memory. The /uploads/*
    // routes are static (no auth required), so a direct <video src=…> works.
    if (isVideo) {
      setViewingFile({
        url: fileUrl,
        name,
        type: 'video',
        directUrl: true, // signal: do NOT revokeObjectURL on close
      });
      return;
    }

    try {
      setViewingFileLoading(true);
      const response = await axios.get(fileUrl, { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);

      if (isImage) {
        setViewingFile({ url, name, type: 'image' });
      } else if (isPdf) {
        window.open(url, '_blank');
      } else {
        // CSV / TXT and other small text files — read as text.
        const text = await blob.text();
        setViewingFile({ content: text, name, type: 'text' });
      }
    } catch (error) {
      console.error('View error:', error);
      toast.error('Failed to view file');
    } finally {
      setViewingFileLoading(false);
    }
  };

  // Close file viewer
  const closeFileViewer = () => {
    // Only revoke blob: URLs created via createObjectURL — direct /uploads/ paths
    // must not be passed to revokeObjectURL.
    if (viewingFile?.url && !viewingFile.directUrl) {
      window.URL.revokeObjectURL(viewingFile.url);
    }
    setViewingFile(null);
  };

  // Download all files for a stage
  const downloadAllFiles = async (mediaList, stage) => {
    try {
      toast.info(`Starting download of ${mediaList.length} files for ${stage}...`)
      
      for (const media of mediaList) {
        await downloadFile(media)
        // Small delay between downloads to prevent overwhelming the browser
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      toast.success(`All ${stage} files downloaded successfully`)
    } catch (error) {
      console.error('Error downloading files:', error)
      toast.error('Failed to download some files')
    }
  }

  const getStageColor = (stage) => {
    const colors = {
      'New Lead': 'bg-blue-100 text-blue-800',
      'Site Visit': 'bg-yellow-100 text-yellow-800',
      'Proposal Sent': 'bg-purple-100 text-purple-800',
      'Agreement Signed': 'bg-green-100 text-green-800',
      'Setup in Progress': 'bg-orange-100 text-orange-800',
      'Go Live': 'bg-red-100 text-red-800'
    }
    return colors[stage] || 'bg-gray-100 text-gray-800'
  }

  const getStageProgress = (stage) => {
    const stages = ['New Lead', 'Site Visit', 'Proposal Sent', 'Agreement Signed', 'Facility Work', 'Post Reki Approved', 'Gym Onboard']
    const currentIndex = stages.indexOf(stage)
    if (currentIndex === -1) return 0
    return ((currentIndex + 1) / stages.length) * 100
  }

  const getNextAllowedStage = () => {
    const stages = ['New Lead', 'Site Visit', 'Proposal Sent', 'Agreement Signed', 'Facility Work', 'Post Reki Approved', 'Gym Onboard']
    const currentIndex = stages.indexOf(gym?.stage)

    if (isBDExecutive) {
      // BD Executive can only move up to Site Visit
      const allowedStages = ['New Lead', 'Site Visit']
      const nextStageIndex = currentIndex + 1
      return nextStageIndex < allowedStages.length ? allowedStages[nextStageIndex] : 'Upload proof required'
    }

    return currentIndex < stages.length - 1 ? stages[currentIndex + 1] : 'Complete'
  }

  const renderGymDetails = () => (
    <div className="space-y-6">
      {/* Header Info */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-4 border border-red-100">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{gym.name}</h3>
            <p className="text-gray-600">{gym.city || gym.location}{gym.pinCode ? `, ${gym.pinCode}` : ''}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStageColor(gym.stage)}`}>
            {gym.stage}
          </span>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
            <p className="text-xs text-gray-500">BD Executive</p>
            <p className="text-sm font-semibold text-gray-900 truncate">{gym.assignedUser?.name || 'Not assigned'}</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
            <p className="text-xs text-gray-500">Target Launch</p>
            <p className="text-sm font-semibold text-gray-900">
              {gym.launch_date ? format(new Date(gym.launch_date), 'MMM dd, yyyy') :
               gym.targetLaunchDate ? format(new Date(gym.targetLaunchDate), 'MMM dd, yyyy') : 'Not set'}
            </p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
            <p className="text-xs text-gray-500">Created</p>
            <p className="text-sm font-semibold text-gray-900">{format(new Date(gym.createdAt), 'MMM dd, yyyy')}</p>
          </div>
          <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
            <p className="text-xs text-gray-500">Last Updated</p>
            <p className="text-sm font-semibold text-gray-900">{gym.updatedAt ? formatDistanceToNow(new Date(gym.updatedAt), { addSuffix: true }) : '-'}</p>
          </div>
        </div>
      </div>

      {/* Full Address */}
      <div>
        <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Full Address
        </h4>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-gray-900 font-medium">{gym.address || 'Address not provided'}</p>
          <p className="text-gray-600 text-sm mt-1">
            {gym.city}{gym.state ? `, ${gym.state}` : ''}{gym.pinCode ? ` - ${gym.pinCode}` : ''}
          </p>
          {(gym.gps_lat && gym.gps_lng) && (
            <a
              href={`https://maps.google.com/?q=${gym.gps_lat},${gym.gps_lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-blue-600 hover:text-blue-800 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              View on Google Maps
            </a>
          )}
        </div>
      </div>

      {/* Owner Information */}
      <div>
        <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Owner Information
        </h4>
        <div className="bg-white border rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-semibold text-lg">
                  {(gym.contact_person || gym.ownerName || 'O').charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500">Owner Name</p>
                <p className="text-gray-900 font-medium">{gym.contact_person || gym.ownerName || 'Not provided'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Owner Contact</p>
                {(gym.ownerContact || gym.phoneNumber) ? (
                  <a href={`tel:${gym.ownerContact || gym.phoneNumber}`} className="text-blue-600 hover:underline font-medium">
                    {gym.ownerContact || gym.phoneNumber}
                  </a>
                ) : (
                  <p className="text-gray-400">Not provided</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Owner Email</p>
                {gym.ownerEmail ? (
                  <a href={`mailto:${gym.ownerEmail}`} className="text-blue-600 hover:underline font-medium break-all">
                    {gym.ownerEmail}
                  </a>
                ) : (
                  <p className="text-gray-400">Not provided</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Gym Phone</p>
                {gym.phone ? (
                  <a href={`tel:${gym.phone}`} className="text-blue-600 hover:underline font-medium">
                    {gym.phone}
                  </a>
                ) : (
                  <p className="text-gray-400">Not provided</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gym Details & Specifications */}
      <div>
        <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Gym Details & Specifications
        </h4>
        <div className="bg-white border rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Gym Size */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Gym Size</p>
              <p className="text-lg font-semibold text-gray-900">
                {gym.size ? `${gym.size.toLocaleString('en-IN')} sq ft` : 'Not specified'}
              </p>
            </div>

            {/* Number of Equipment */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Equipment Count</p>
              <p className="text-lg font-semibold text-gray-900">
                {gym.equipmentCount || gym.numEquipment || 'Not specified'}
              </p>
            </div>

            {/* Operating Hours */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Operating Hours</p>
              <p className="text-lg font-semibold text-gray-900">
                {gym.operatingHours || 'Not specified'}
              </p>
            </div>

            {/* Current Member Count */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Current Members</p>
              <p className="text-lg font-semibold text-gray-900">
                {gym.currentMemberCount || gym.memberCount || 'Not specified'}
              </p>
            </div>

            {/* Monthly Revenue */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Monthly Revenue</p>
              <p className="text-lg font-semibold text-gray-900">
                {gym.monthlyRevenue ? `₹${Number(gym.monthlyRevenue).toLocaleString('en-IN')}` : 'Not specified'}
              </p>
            </div>

            {/* Staff Count */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Staff Count</p>
              <p className="text-lg font-semibold text-gray-900">
                {gym.staffCount || 'Not specified'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Information */}
      <div>
        <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Financial Information
        </h4>
        <div className="bg-white border rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Proposal Amount */}
            <div className="bg-green-50 rounded-lg p-3 border border-green-100">
              <p className="text-xs text-green-600">Proposal Amount</p>
              <p className="text-xl font-bold text-green-700">
                {gym.proposalAmount ? `₹${Number(gym.proposalAmount).toLocaleString('en-IN')}` : '-'}
              </p>
            </div>

            {/* CTO Amount */}
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <p className="text-xs text-blue-600">CTO Amount</p>
              <p className="text-xl font-bold text-blue-700">
                {gym.ctoAmount ? `₹${Number(gym.ctoAmount).toLocaleString('en-IN')}` : '-'}
              </p>
            </div>

            {/* Closer Percentage */}
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
              <p className="text-xs text-purple-600">Closer Percentage</p>
              <p className="text-xl font-bold text-purple-700">
                {gym.closerPercentage ? `${gym.closerPercentage}%` : '-'}
              </p>
            </div>

            {/* Rent Amount */}
            <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
              <p className="text-xs text-orange-600">Rent Amount</p>
              <p className="text-xl font-bold text-orange-700">
                {gym.rentAmount ? `₹${Number(gym.rentAmount).toLocaleString('en-IN')}` : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* External Links */}
      {(gym.gmbUrl || gym.websiteUrl || gym.instagramUrl || gym.facebookUrl) && (
        <div>
          <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            External Links
          </h4>
          <div className="bg-white border rounded-lg p-4">
            <div className="flex flex-wrap gap-3">
              {gym.gmbUrl && (
                <a
                  href={gym.gmbUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                  </svg>
                  Google My Business
                </a>
              )}
              {gym.websiteUrl && (
                <a
                  href={gym.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  Website
                </a>
              )}
              {gym.instagramUrl && (
                <a
                  href={gym.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-pink-50 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                  Instagram
                </a>
              )}
              {gym.facebookUrl && (
                <a
                  href={gym.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  Facebook
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Visit History */}
      {gym.visitLogs && gym.visitLogs.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-700 mb-2">Visit History</h4>
          <div className="bg-white border rounded-lg p-3 space-y-3">
            {gym.visitLogs.map((visit, index) => (
              <div key={visit.id || index} className={`flex items-start gap-3 ${index !== gym.visitLogs.length - 1 ? 'pb-3 border-b border-gray-100' : ''}`}>
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                  visit.visitType === 'owner_met' ? 'bg-green-500' :
                  visit.visitType === 'owner_not_met' ? 'bg-red-500' :
                  visit.visitType === 'photos_only' ? 'bg-blue-500' : 'bg-gray-400'
                }`}>
                  {visit.attemptNumber || index + 1}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        visit.visitType === 'owner_met' ? 'bg-green-100 text-green-800' :
                        visit.visitType === 'owner_not_met' ? 'bg-red-100 text-red-800' :
                        visit.visitType === 'photos_only' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {visit.visitType === 'owner_met' ? 'Owner Met' :
                         visit.visitType === 'owner_not_met' ? 'Owner Not Available' :
                         visit.visitType === 'photos_only' ? 'Photos Only' : visit.visitType}
                      </span>
                      {visit.notes && <p className="text-sm text-gray-600 mt-1">{visit.notes}</p>}
                    </div>
                    <span className="text-xs text-gray-500">
                      {visit.visitDate ? format(new Date(visit.visitDate), 'MMM dd, yyyy HH:mm') : '-'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assigned Manager */}
      {gym.assignedManager && (
        <div>
          <h4 className="font-medium text-gray-700 mb-2">Assigned Manager</h4>
          <div className="bg-white border rounded-lg p-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <span className="text-indigo-600 font-medium">
                  {gym.assignedManager.name?.charAt(0)?.toUpperCase() || 'M'}
                </span>
              </div>
              <div>
                <p className="text-gray-900 font-medium">{gym.assignedManager.name}</p>
                <p className="text-gray-600 text-sm">{gym.assignedManager.email}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remarks */}
      {gym.remarks && (
        <div>
          <h4 className="font-medium text-gray-700 mb-2">Remarks</h4>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-gray-800 text-sm whitespace-pre-wrap">{gym.remarks}</p>
          </div>
        </div>
      )}

      {/* Internal Notes Preview */}
      {gym.internalNotes && (
        <div>
          <h4 className="font-medium text-gray-700 mb-2">Internal Notes Preview</h4>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-gray-800 text-sm whitespace-pre-wrap line-clamp-3">{gym.internalNotes}</p>
            <button
              onClick={() => setActiveTab('notes')}
              className="text-blue-600 hover:underline text-sm mt-2"
            >
              View all notes →
            </button>
          </div>
        </div>
      )}
    </div>
  )

  const renderAcquisitionProgress = () => (
    <div className="space-y-6">
      {/* Progress Bar */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-medium text-gray-700">Acquisition Progress</h4>
          <span className="text-sm text-gray-600">{Math.round(getStageProgress(gym.stage))}% Complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${getStageProgress(gym.stage)}%` }}
          ></div>
        </div>
      </div>

      {/* Current Stage */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">Current Stage</h4>
        <div className="flex justify-between items-center">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStageColor(gym.stage)}`}>
            {gym.stage}
          </span>
          <span className="text-sm text-blue-600">
            Next: {getNextAllowedStage()}
          </span>
        </div>
      </div>

      {/* Stage Timeline */}
      <div>
        <h4 className="font-medium text-gray-700 mb-3">Stage History</h4>
        <div className="space-y-3">
          {gym.approvals && gym.approvals.length > 0 ? (
            gym.approvals.map((approval, index) => (
              <div key={approval.id} className="flex items-start space-x-3 pb-3 border-b border-gray-100 last:border-b-0">
                <div className="flex-shrink-0 w-3 h-3 bg-green-500 rounded-full mt-1"></div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{approval.type.replace('_', ' ').toUpperCase()}</p>
                      <p className="text-sm text-gray-600">{approval.comments}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">
                        {format(new Date(approval.createdAt), 'MMM dd, yyyy')}
                      </p>
                      <p className="text-xs text-gray-400">
                        {approval.approvedBy ? approval.approvedBy.name : 'System'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-center py-4">No stage history available</p>
          )}
        </div>
      </div>
    </div>
  )

  const renderChecklistSummary = () => {
    if (!isManager) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-gray-500">Checklist access requires Manager level or above</p>
        </div>
      )
    }

    const totalTasks = gym.checklistItems?.length || 0
    const completedTasks = gym.checklistItems?.filter(item => item.status === 'completed').length || 0
    const approvedTasks = gym.checklistItems?.filter(item => item.status === 'approved').length || 0

    return (
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{totalTasks}</div>
            <div className="text-sm text-blue-800">Total Tasks</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{completedTasks}</div>
            <div className="text-sm text-green-800">Completed</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{approvedTasks}</div>
            <div className="text-sm text-purple-800">Approved</div>
          </div>
        </div>

        {/* Checklist Items */}
        {totalTasks > 0 ? (
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Checklist Items</h4>
            <div className="space-y-2">
              {gym.checklistItems.map((item, index) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full ${
                      item.status === 'approved' ? 'bg-green-500' :
                      item.status === 'completed' ? 'bg-blue-500' :
                      'bg-gray-300'
                    }`}></div>
                    <span className="text-gray-900">{item.title}</span>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    item.status === 'approved' ? 'bg-green-100 text-green-800' :
                    item.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No checklist items found</p>
        )}
      </div>
    )
  }

  // Helper to get the "from" stage based on the "to" stage for proof labels
  const getTransitionLabel = (toStage) => {
    const transitions = {
      'Site Visit': 'New Lead → Site Visit',
      'Proposal Sent': 'Site Visit → Proposal Sent',
      'Agreement Signed': 'Proposal Sent → Agreement Signed',
      'Facility Work': 'Agreement Signed → Facility Work',
      'Post Reki Approved': 'Facility Work → Post Reki Approved',
      'Gym Onboard': 'Post Reki Approved → Gym Onboard'
    };
    return transitions[toStage] || toStage;
  };

  const renderProofOfWork = () => {
    const stageProofMedia = gym.media?.filter(media => media.stage) || [];

    // Debug log to check media data
    console.log('All media for gym:', gym.media);
    console.log('Stage proof media:', stageProofMedia);

    // Group media by stage for better organization
    const mediaByStage = stageProofMedia.reduce((acc, media) => {
      const stage = media.stage || 'Unknown Stage';
      if (!acc[stage]) {
        acc[stage] = [];
      }
      acc[stage].push(media);
      return acc;
    }, {});
    const stageOrder = ['New Lead', 'Site Visit', 'Proposal Sent', 'Agreement Signed', 'Facility Work', 'Post Reki Approved', 'Gym Onboard'];
    const sortedStages = Object.keys(mediaByStage).sort((a, b) => {
      const indexA = stageOrder.indexOf(a);
      const indexB = stageOrder.indexOf(b);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });
    
    return (
      <div className="space-y-6">
        {/* Stage-wise Proof Organization */}
        <div>
          <h4 className="font-medium text-gray-700 mb-4">Stage Transition Proofs</h4>
          {sortedStages.length > 0 ? (
            <div className="space-y-6">
              {sortedStages.map(stage => (
                <div key={stage} className="border border-gray-200 rounded-lg p-4">
                  {/* Stage Header with Transition Label */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex flex-col">
                      <h5 className={`text-sm font-semibold px-3 py-1 rounded-full ${getStageColor(stage)}`}>
                        {getTransitionLabel(stage)}
                      </h5>
                      <span className="text-xs text-gray-500 mt-1 ml-1">Proof for: {stage}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">
                        {mediaByStage[stage].length} proof{mediaByStage[stage].length !== 1 ? 's' : ''}
                      </span>
                      {mediaByStage[stage].length > 1 && (
                        <button
                          onClick={() => downloadAllFiles(mediaByStage[stage], stage)}
                          className="text-blue-500 hover:underline text-xs"
                        >
                          Download All
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Photos for this stage */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    {mediaByStage[stage].map((media, index) => {
                      const mediaUrl = getMediaUrl(media, 'stage-proofs');
                      return (
                        <div key={media.id} className="relative group">
                          {media.type === 'IMAGE' || media.mimetype?.startsWith('image/') ? (
                            <img
                              src={mediaUrl}
                              alt={`${stage} proof ${index + 1}`}
                              className="w-full h-20 object-cover rounded-lg border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                              onClick={() => viewFile(media)}
                              onError={(e) => {
                                console.error('Image failed to load:', e.target.src);
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIxIDNIMy4wMDNDMi40NSAzIDIgMy40NSAyIDMuOTkzVjIwLjAwN0MyIDIwLjU1IDIuNDUgMjEgMy4wMDMgMjFIMjEuMDIzQzIxLjU1IDIxIDIyIDIwLjU1IDIyIDIwLjAwN0YzLjk5M0MyMiAzLjQ1IDIxLjU1IDMgMjEgM1pNMjAgMTlINFY1SDE5TDIwIDE5Wk0xNy4xIDExLjlMMTMuNzUgMTcuODVMMTIuMiAxNi4yNUMxMS44NSAxNS45IDExLjMgMTUuOSAxMC45NSAxNi4yNUMxMC42IDE2LjYgMTAuNiAxNy4xNSAxMC45NSAxNy41TDEzIDIxLjVMMTggMTNMMTcuMSAxMS45WiIgZmlsbD0iIzlEQTJBNiIvPgo8L3N2Zz4='
                              }}
                              title={media.description || `Uploaded: ${format(new Date(media.createdAt), 'MMM dd, yyyy HH:mm')}`}
                            />
                          ) : (
                            <div
                              className="w-full h-20 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 cursor-pointer"
                              title={media.description || `Uploaded: ${format(new Date(media.createdAt), 'MMM dd, yyyy HH:mm')}`}
                              onClick={() => viewFile(media)}
                            >
                              <span className="text-xs text-gray-500">{media.originalName || media.filename || 'File'}</span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200"></div>

                          {/* Action buttons - always visible */}
                          <div className="absolute top-1 right-1 flex space-x-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                viewFile(media)
                              }}
                              disabled={viewingFileLoading}
                              className="bg-blue-600 text-white p-1.5 rounded-full hover:bg-blue-700 transition-colors shadow-md"
                              title="View"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                downloadFile(media)
                              }}
                              className="bg-green-600 text-white p-1.5 rounded-full hover:bg-green-700 transition-colors shadow-md"
                              title="Download"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </button>
                            {canDeleteMedia(media) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  deleteMedia(media)
                                }}
                                className="bg-red-600 text-white p-1.5 rounded-full hover:bg-red-700 transition-colors shadow-md"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-500">No proof photos uploaded yet</p>
              <p className="text-xs text-gray-400 mt-1">Proofs will be organized by stage when uploaded</p>
            </div>
          )}
        </div>
        {/* Summary Statistics */}
        {sortedStages.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Proof Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-blue-600">{sortedStages.length}</div>
                <div className="text-xs text-blue-800">Stages with Proof</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-green-600">{stageProofMedia.length}</div>
                <div className="text-xs text-green-800">Total Proofs</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-purple-600">
                  {stageProofMedia.length > 0 ?
                    format(new Date(Math.max(...stageProofMedia.map(m => new Date(m.createdAt)))), 'MMM dd') :
                    '--'}
                </div>
                <div className="text-xs text-purple-800">Latest Upload</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderNotes = () => {
    const canEditNotes = isBDExecutive && gym.assignedUserId === user.id

    return (
      <div className="space-y-6">
        {/* Add New Note */}
        {canEditNotes && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-3">Add Visit Notes</h4>
            {!editingNotes ? (
              <button
                onClick={() => setEditingNotes(true)}
                className="w-full p-3 text-left text-gray-600 bg-white border border-dashed border-gray-300 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                + Click to add visit notes...
              </button>
            ) : (
              <div className="space-y-3">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Enter your visit notes, observations, or updates..."
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setEditingNotes(false)
                      setNewNote('')
                    }}
                    disabled={savingNote}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleNoteSave}
                    disabled={savingNote || !newNote.trim()}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {savingNote ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Saving...
                      </>
                    ) : (
                      'Save Note'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notes History */}
        <div>
          <h4 className="font-medium text-gray-700 mb-3">Visit Notes History</h4>
          {gym.internalNotes ? (
            <div className="bg-white border rounded-lg p-4">
              <pre className="whitespace-pre-wrap text-sm text-gray-900 leading-relaxed">
                {gym.internalNotes}
              </pre>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-500">No visit notes added yet</p>
            </div>
          )}
        </div>
      </div>
    )
  }

  const getRekiStatusBadge = (status) => {
    const statusConfig = {
      'pending': { bg: 'bg-white/20', text: 'text-white', label: 'Pending', icon: '⏳' },
      'in_progress': { bg: 'bg-blue-400/30', text: 'text-white', label: 'In Progress', icon: '🔄' },
      'submitted': { bg: 'bg-yellow-400/30', text: 'text-white', label: 'Submitted', icon: '📤' },
      'approved': { bg: 'bg-green-400/30', text: 'text-white', label: 'Approved', icon: '✅' },
      'rejected': { bg: 'bg-red-400/30', text: 'text-white', label: 'Rejected', icon: '❌' }
    }
    const config = statusConfig[status] || statusConfig['pending']
    return (
      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text} inline-flex items-center gap-1`}>
        <span>{config.icon}</span>
        {config.label}
      </span>
    )
  }

  const renderRekiChecklist = (rekiData, type) => {
    if (rekiLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      )
    }

    if (!rekiData) {
      return (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-600 mb-2">
            No {type === 'pre_reki' ? 'Pre-Reki' : 'Post-Reki'} Checklist Yet
          </h4>
          <p className="text-sm text-gray-500">
            {type === 'pre_reki'
              ? 'Pre-Reki checklist will be created when the gym reaches Facility Work stage.'
              : 'Post-Reki checklist will be created after Pre-Reki is completed.'}
          </p>
        </div>
      )
    }

    const completedCount = rekiData.items?.filter(item => item.isCompleted).length || 0
    const totalCount = rekiData.items?.length || 0
    const issuesCount = rekiData.items?.filter(item => item.hasIssue).length || 0
    const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

    // Group items by category
    const itemsByCategory = rekiData.items?.reduce((acc, item) => {
      const category = item.category || 'Other'
      if (!acc[category]) acc[category] = []
      acc[category].push(item)
      return acc
    }, {}) || {}

    return (
      <div className="space-y-5">
        {/* Header with Status and Progress */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h4 className="font-bold text-lg text-gray-900">
                {type === 'pre_reki' ? 'Pre-Reki Checklist' : 'Post-Reki Checklist'}
              </h4>
              <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                rekiData.status === 'approved' ? 'bg-green-100 text-green-700' :
                rekiData.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                rekiData.status === 'submitted' ? 'bg-yellow-100 text-yellow-700' :
                rekiData.status === 'rejected' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {rekiData.status === 'approved' ? 'Approved' :
                 rekiData.status === 'in_progress' ? 'In Progress' :
                 rekiData.status === 'submitted' ? 'Submitted' :
                 rekiData.status === 'rejected' ? 'Rejected' : 'Pending'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {type === 'post_reki' && isManager && (
                <button
                  onClick={resetPostReki}
                  className="px-3 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                  title="Reset Post-Reki to sync from Pre-Reki"
                >
                  Reset from Pre-Reki
                </button>
              )}
              <span className="text-lg font-bold text-indigo-600">{progressPercent}% Complete</span>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-sm text-gray-600">
            <span>{completedCount} of {totalCount} items completed</span>
            {issuesCount > 0 && (
              <span className="text-red-600 font-medium">{issuesCount} issues flagged</span>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
            <div className="text-xl font-bold text-blue-600">{totalCount}</div>
            <div className="text-xs text-blue-700">Total Items</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center border border-green-100">
            <div className="text-xl font-bold text-green-600">{completedCount}</div>
            <div className="text-xs text-green-700">Completed</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center border border-yellow-100">
            <div className="text-xl font-bold text-yellow-600">{totalCount - completedCount}</div>
            <div className="text-xs text-yellow-700">Pending</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center border border-red-100">
            <div className="text-xl font-bold text-red-600">{issuesCount}</div>
            <div className="text-xs text-red-700">Issues</div>
          </div>
        </div>

        {/* Items by Category */}
        <div className="space-y-4">
          {Object.entries(itemsByCategory).map(([category, items]) => (
            <div key={category} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              {/* Category Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h5 className="font-semibold text-gray-800 text-sm">{category}</h5>
                  <span className="text-xs font-medium px-2 py-1 bg-white rounded-full text-gray-600 shadow-sm">
                    {items.filter(i => i.isCompleted).length}/{items.length}
                  </span>
                </div>
              </div>

              {/* Items List */}
              <div className="divide-y divide-gray-100">
                {items.map((item) => (
                  <div key={item.id} className={`px-4 py-3 transition-colors ${
                    item.isCompleted ? 'bg-green-50/30' : item.hasIssue ? 'bg-red-50/30' : 'hover:bg-gray-50'
                  }`}>
                    <div className="flex items-start gap-3">
                      {/* Status Indicator */}
                      <div className="flex-shrink-0 mt-0.5">
                        {item.isCompleted ? (
                          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center shadow-sm">
                            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ) : item.hasIssue ? (
                          <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center shadow-sm">
                            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                          </div>
                        )}
                      </div>

                      {/* Item Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2">
                          <span className={`font-medium text-sm ${
                            item.isCompleted
                              ? 'text-green-700'
                              : item.hasIssue
                                ? 'text-red-700'
                                : 'text-gray-900'
                          }`}>
                            {item.title}
                          </span>
                          {item.isMandatory && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-600 rounded-full">
                              Required
                            </span>
                          )}
                          {item.isCompleted && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-600 rounded-full">
                              Done
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                        )}

                        {/* Issue Notes */}
                        {item.hasIssue && item.issueNotes && (
                          <div className="mt-2 p-2.5 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-xs text-red-700">
                              <span className="font-semibold">Issue: </span>{item.issueNotes}
                            </p>
                          </div>
                        )}

                        {/* Completion Notes */}
                        {item.isCompleted && item.completionNotes && (
                          <div className="mt-2 p-2.5 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-xs text-green-700">
                              <span className="font-semibold">Note: </span>{item.completionNotes}
                            </p>
                          </div>
                        )}

                        {/* Media */}
                        {item.media && item.media.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {item.media.map((media, idx) => {
                              const rekiMediaUrl = getMediaUrl(media, 'reki');
                              return (
                                <div key={media.id || idx} className="relative group">
                                  {media.mimetype?.startsWith('image/') ? (
                                    <img
                                      src={rekiMediaUrl}
                                      alt={`Proof ${idx + 1}`}
                                      className="w-14 h-14 object-cover rounded-lg border-2 border-gray-200 cursor-pointer hover:border-indigo-400 hover:shadow-lg transition-all"
                                      onClick={() => viewFile(media, 'reki')}
                                      onError={(e) => {
                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNCAxNmw0LjU4Ni00LjU4NmEyIDIgMCAwMTIuODI4IDBMMTYgMTZtLTItMmwxLjU4Ni0xLjU4NmEyIDIgMCAwMTIuODI4IDBMMjAgMTRtLTYtNmguMDFNNiAyMGgxMmEyIDIgMCAwMDItMlY2YTIgMiAwIDAwLTItMkg2YTIgMiAwIDAwLTIgMnYxMmEyIDIgMCAwMDIgMnoiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48L3N2Zz4='
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-gray-200 cursor-pointer hover:border-indigo-400"
                                      onClick={() => viewFile(media, 'reki')}
                                    >
                                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Completion Info */}
                        {item.isCompleted && item.completedAt && (
                          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {format(new Date(item.completedAt), 'MMM dd, yyyy HH:mm')}
                            {item.completedBy && ` • ${item.completedBy.name}`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Approval Info */}
        {rekiData.status === 'approved' && rekiData.approvedBy && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium text-green-800">
                Approved by {rekiData.approvedBy.name}
              </span>
              {rekiData.approvedAt && (
                <span className="text-xs text-green-600">
                  on {format(new Date(rekiData.approvedAt), 'MMM dd, yyyy')}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {gym ? `${gym.name} : ${gym.address} - Details` : 'Gym Details'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs - Scrollable container */}
        <div className="border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            {[
              { id: 'details', label: 'Details', icon: '🏢' },
              { id: 'progress', label: 'Progress', icon: '📈' },
              { id: 'checklist', label: 'Checklist', icon: '✅' },
              { id: 'preReki', label: 'Pre-Reki', icon: '📋' },
              { id: 'postReki', label: 'Post-Reki', icon: '✨' },
              { id: 'proof', label: 'Proof', icon: '📸' },
              { id: 'notes', label: 'Notes', icon: '📝' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 min-w-fit ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 bg-white'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                <span className="text-sm">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : gym ? (
            <>
              {activeTab === 'details' && renderGymDetails()}
              {activeTab === 'progress' && renderAcquisitionProgress()}
              {activeTab === 'checklist' && renderChecklistSummary()}
              {activeTab === 'preReki' && renderRekiChecklist(preRekiData, 'pre_reki')}
              {activeTab === 'postReki' && renderRekiChecklist(postRekiData, 'post_reki')}
              {activeTab === 'proof' && renderProofOfWork()}
              {activeTab === 'notes' && renderNotes()}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Failed to load gym details</p>
            </div>
          )}
        </div>
      </div>

      {/* File Viewer Modal */}
      {viewingFile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Viewer Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {viewingFile.name}
              </h3>
              <button
                onClick={closeFileViewer}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Viewer Content */}
            <div className="flex-1 overflow-auto p-4 bg-gray-100">
              {viewingFile.type === 'image' ? (
                <div className="flex items-center justify-center min-h-[400px]">
                  <img
                    src={viewingFile.url}
                    alt={viewingFile.name}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                  />
                </div>
              ) : viewingFile.type === 'video' ? (
                <div className="flex items-center justify-center min-h-[400px] bg-black rounded-lg">
                  <video
                    src={viewingFile.url}
                    controls
                    autoPlay
                    playsInline
                    className="max-w-full max-h-[70vh] rounded-lg"
                  >
                    Your browser does not support the video tag.
                    <a href={viewingFile.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">
                      Open video in new tab
                    </a>
                  </video>
                </div>
              ) : viewingFile.type === 'text' ? (
                <div className="bg-white rounded-lg p-4 shadow-inner">
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono overflow-x-auto">
                    {viewingFile.content}
                  </pre>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">Cannot preview this file type</p>
                </div>
              )}
            </div>

            {/* Viewer Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={closeFileViewer}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for file viewing */}
      {viewingFileLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 shadow-xl flex items-center space-x-3">
            <LoadingSpinner size="md" />
            <span className="text-gray-700">Loading file...</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default GymDetailsModal 
